/**
 * 断言告警条：展示断言引擎（Mock 后端用内置正则复刻）命中的己方口径错误。
 * severity 决定色彩；告警文本包含纠错口径与命中片段。
 */
import type { AssertionAlert } from '@zongheng/core-schema';
import { sideLabel } from '@zongheng/core-schema';
import { Badge, cx, EmptyState } from '../ui/primitives.tsx';

const SEVERITY_LABEL: Record<AssertionAlert['severity'], string> = {
  INFO: '提示',
  WARN: '注意',
  VIOLATION: '违规',
};

export function AlertBar({ alerts }: { alerts: AssertionAlert[] }) {
  return (
    <section
      data-testid="hud-alerts"
      aria-label="断言告警"
      className="rounded-xl border border-white/10 bg-bg-surface/80 p-3 backdrop-blur"
    >
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold tracking-wide text-slate-100">断言告警 · 己方口径</h2>
        <Badge tone={alerts.length > 0 ? 'violation' : 'pass'}>
          {alerts.length > 0 ? `${alerts.length} 条` : '干净'}
        </Badge>
      </div>
      {alerts.length === 0 ? (
        <EmptyState title="暂无口径错误" detail="提交发言文本后，端侧断言引擎会实时扫描数据张冠李戴。" />
      ) : (
        <ul className="space-y-2">
          {alerts.map((alert) => (
            <li
              key={alert.id}
              data-testid="hud-alert"
              data-severity={alert.severity}
              className={cx(
                'rounded-lg border px-3 py-2',
                alert.severity === 'VIOLATION'
                  ? 'border-assert-violation/40 bg-assert-violation/10'
                  : alert.severity === 'WARN'
                    ? 'border-clock-warning/40 bg-clock-warning/10'
                    : 'border-white/10 bg-white/5',
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={alert.severity === 'VIOLATION' ? 'violation' : 'warning'}>
                  {SEVERITY_LABEL[alert.severity]}
                </Badge>
                <span className="text-[11px] text-slate-500">
                  环节 {alert.stageIndex} · {sideLabel(alert.side)}
                </span>
                {alert.evidenceId && (
                  <span className="mono text-[10px] text-slate-600">{alert.evidenceId}</span>
                )}
              </div>
              <p className="mt-1 text-sm font-medium text-slate-100">{alert.message}</p>
              <p className="clamp-3 mt-1 text-[11px] leading-relaxed text-slate-400">
                命中片段：{alert.snippet}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
